import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';
import { Container, Table, Button, Modal, Form, Spinner, Badge, Card, Alert, Pagination } from 'react-bootstrap';
import { Ticket, Plus, MessageSquare, X, Send, Clock } from 'lucide-react';
import Sidebar from '../components/Layout/Sidebar';
import api from '../services/api';
import './TicketsPage.css';
const TicketsPage = () => {

  const [submitting, setSubmitting] = useState(false);
  const [tickets, setTickets] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [ticketStatuses, setTicketStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [newTicket, setNewTicket] = useState({ subject: '', id_type: '', message: '' });
  const [newMessage, setNewMessage] = useState('');
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 30;
  useEffect(() => {
    loadTickets();
    loadTicketTypes();
    loadTicketStatuses();
  }, []);
  const sanitizeHTML = (html) => {
    return { __html: DOMPurify.sanitize(html) };
  };
  const loadTickets = async (page = 1) => {
    try {
      setLoading(true);
      setCurrentPage(page);
      const response = await api.get('/tickets', { params: { limit: itemsPerPage, offset: (page - 1) * itemsPerPage } });
      setTickets(response.data.tickets);
      setTotalPages(Math.ceil(response.data.total / itemsPerPage));
    } catch (error) {
      console.error('Load tickets error:', error);
      setError('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };
  const loadTicketTypes = async () => {
    try {
      const response = await api.get('/tickets/types');
      setTicketTypes(response.data);
    } catch (error) {
      console.error('Load ticket types error:', error);
    }
  };
  const loadTicketStatuses = async () => {
    try {
      const response = await api.get('/tickets/statuses');
      setTicketStatuses(response.data);
    } catch (error) {
      console.error('Load ticket statuses error:', error);
    }
  };
  const handleCreateTicket1 = async () => {
    if (!newTicket.subject || !newTicket.id_type) {
      setError('Please fill in Subject and Type fields');
      return;
    }
    try {
      await api.post('/tickets', newTicket);
      setShowCreateModal(false);
      setNewTicket({ subject: '', id_type: '', message: '' });
      setError('');
      loadTickets();
    } catch (error) {
      console.error('Create ticket error:', error);
      setError('Failed to create ticket');
    }
  };
  const handleCreateTicket = async () => {
    if (!newTicket.subject || !newTicket.id_type) {
      setError('Please fill in Subject and Type fields');
      return;
    }
    if (submitting) return;
    setSubmitting(true);
    setError('');
    try {
      await api.post('/tickets', newTicket);
      setShowCreateModal(false);
      setNewTicket({ subject: '', id_type: '', message: '' });
      loadTickets();
    } catch (error) {
      console.error('Create ticket error:', error);
      setError(error.response?.data?.message || 'Failed to create ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  const handleOpenTicket = async (ticket) => {
    setSelectedTicket(ticket);
    setShowDetailsModal(true);
    setMessagesLoading(true);
    try {
      const response = await api.get(`/tickets/${ticket.id}/messages`);
      setTicketMessages(response.data);
    } catch (error) {
      console.error('Load messages error:', error);
      setError('Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  };
  const handleAddMessage = async () => {
    if (!newMessage.trim()) {
      setError('Message cannot be empty');
      return;
    }
    try {
      await api.post(`/tickets/${selectedTicket.id}/messages`, { message: newMessage });
      setNewMessage('');
      setError('');
      const response = await api.get(`/tickets/${selectedTicket.id}/messages`);
      setTicketMessages(response.data);
      loadTickets();
    } catch (error) {
      console.error('Add message error:', error);
      setError('Failed to add message');
    }
  };
    const renderPagination = () => {
    if (totalPages <= 1) return null;
    let items = [];
    const maxVisible = 10;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);
    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }
    if (startPage > 1) {
      items.push(
        <Pagination.First key="first" onClick={() => handlePageChange(1)} />
      );
      items.push(
        <Pagination.Prev key="prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
      );
    }
    for (let page = startPage; page <= endPage; page++) {
      items.push(
        <Pagination.Item
          key={page}
          active={page === currentPage}
          onClick={() => handlePageChange(page)}
        >
          {page}
        </Pagination.Item>
      );
    }
    if (endPage < totalPages) {
      items.push(
        <Pagination.Next key="next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} />
      );
      items.push(
        <Pagination.Last key="last" onClick={() => handlePageChange(totalPages)} />
      );
    }
    return <Pagination className="justify-content-center mt-4">{items}</Pagination>;
  };
  const handlePageChange = (page) => {
    loadTickets(page);  
  };
  const handleCloseTicket = async () => {
    if (!window.confirm('Are you sure you want to close this ticket?')) {
      return;
    }
    try {
      await api.post(`/tickets/${selectedTicket.id}/close`);
      setShowDetailsModal(false);
      loadTickets();
    } catch (error) {
      console.error('Close ticket error:', error);
      setError('Failed to close ticket');
    }
  };
  const getStatusBadge = (statusId, statusName) => {
    const colorMap = {
      1: 'primary',
      3: 'secondary',
      5: 'warning',
      6: 'info',
      8: 'success',
      9: 'danger',
      10: 'danger',
      11: 'success'
    };
    const variant = colorMap[statusId] || 'secondary';
    return <Badge bg={variant}>{statusName || 'Unknown'}</Badge>;
  };
  return (
    <div className="d-flex">
      <Sidebar />
      <Container className="tickets-container p-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <Ticket size={32} className="me-3 text-primary" />
            <h2 className="mb-0">Support Tickets</h2>
          </div>
          <Button variant="primary" onClick={() => setShowCreateModal(true)}>
            <Plus size={20} className="me-2" />
            New Ticket
          </Button>
        </div>
        {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-3">Loading tickets...</p>
          </div>
        ) : tickets.length === 0 ? (
          <Card className="text-center py-5">
            <Card.Body>
              <Ticket size={48} className="text-muted mb-3" />
              <h5>No tickets yet</h5>
              <p className="text-muted">Create your first support ticket to get started</p>
              <Button variant="primary" onClick={handleCreateTicket} disabled={submitting}>
              {submitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Send size={16} className="me-2" />
                  Create Ticket
                </>
              )}
            </Button>
            </Card.Body>
          </Card>
        ) : (
          <Table hover responsive className="tickets-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Subject</th>
                <th>Type</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last Update</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map((ticket) => (
                <tr key={ticket.id} className="ticket-row">
                  <td><strong>#{ticket.id}</strong></td>
                  <td>{ticket.subject}</td>
                  <td>{ticket.tt_type_name}</td>
                  <td>{getStatusBadge(ticket.status, ticket.status_name)}</td>
                  <td>{new Date(ticket.add_date).toLocaleString()}</td>
                  <td>{new Date(ticket.last_update).toLocaleString()}</td>
                  <td>
                    <Button size="sm" variant="outline-primary" onClick={() => handleOpenTicket(ticket)}>
                      <MessageSquare size={16} className="me-1" />
                      View
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
        {renderPagination()}
        <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              <div className="d-flex align-items-center">
                <Plus size={24} className="me-2" />
                Create New Support Ticket
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Subject *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="Enter ticket subject"
                  value={newTicket.subject}
                  onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Ticket Type *</Form.Label>
                <Form.Select
                  value={newTicket.id_type}
                  onChange={(e) => setNewTicket({ ...newTicket, id_type: e.target.value })}
                >
                  <option value="">Select type...</option>
                  {ticketTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.tt_type_name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Initial Message</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  placeholder="Describe your issue or question here..."
                  value={newTicket.message}
                  onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                />
                <Form.Text className="text-muted">
                  Optional: Add your first message to start the conversation
                </Form.Text>
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleCreateTicket}>
              <Send size={16} className="me-2" />
              Create Ticket
            </Button>
          </Modal.Footer>
        </Modal>
        <Modal show={showDetailsModal} onHide={() => setShowDetailsModal(false)} size="lg" centered className="ticket-details-modal">
          <Modal.Header closeButton>
            <Modal.Title>
              <div className="d-flex align-items-center">
                <MessageSquare size={24} className="me-2" />
                {selectedTicket && (
                  <div>
                    <div>Ticket #{selectedTicket.id} - {selectedTicket.subject}</div>
                    <small className="text-muted">{selectedTicket.tt_type_name}</small>
                  </div>
                )}
              </div>
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="messages-container">
              {messagesLoading ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                  <p className="mt-2">Loading messages...</p>
                </div>
              ) : ticketMessages.length === 0 ? (
                <Alert variant="info">No messages yet. Start the conversation!</Alert>
              ) : (
                ticketMessages.map((msg, idx) => (
                  <Card key={idx} className="message-card mb-3">
                    <Card.Body>
                      <div 
                        className="message-body" 
                        dangerouslySetInnerHTML={sanitizeHTML(msg.mail_body)}
                      />
                      <div className="message-date">
                        <Clock size={14} className="me-1" />
                        {new Date(msg.add_date).toLocaleString()}
                      </div>
                    </Card.Body>
                  </Card>
                ))
              )}
            </div>
            {selectedTicket && selectedTicket.status !== 3 && (
              <div className="add-message-section mt-4">
                <Form.Group>
                  <Form.Label>Add Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    placeholder="Type your message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                  />
                </Form.Group>
                <div className="d-flex gap-2 mt-3">
                  <Button variant="primary" onClick={handleAddMessage}>
                    <Send size={16} className="me-2" />
                    Send Message
                  </Button>
                  <Button variant="danger" onClick={handleCloseTicket}>
                    <X size={16} className="me-2" />
                    Close Ticket
                  </Button>
                </div>
              </div>
            )}
            {selectedTicket && selectedTicket.status === 3 && (
              <Alert variant="secondary" className="mt-3">This ticket is closed</Alert>
            )}
          </Modal.Body>
        </Modal>
      </Container>
    </div>
  );
};
export default TicketsPage;